import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Tab, MAX_UPLOAD_SIZE_BYTES } from '../types';
import { PlatformFormatCombo } from '../components/platformDefinitions';
import {
  apiPostCropImage,
  apiPostStartThread, apiPostStartThreadWithPrompt,
  apiDeleteThread, apiDeletePicture, apiPatchThread,
} from 'services/apiService';
import { apiPostGenerate, apiPostCancelGeneration, apiPostRegionalEdit, apiPostReformat } from '../../../services/api/generationApi';
import { useJobStatus } from '../../../hooks/useJobStatus';
import { useActiveJobs, ActiveJob } from '../../../hooks/useActiveJobs';
import { useImagesFromFirestore, togglePictureStarred } from 'hooks/useMediaFromFirestore';
import { useProjectsWithThreadsRealtime } from 'hooks/useMyThreadsRealtime';
import { downloadFromSignedUrl } from '../../../utils/saveFileWithDialog';
import JSZip from 'jszip';

import { useSettings } from './SettingsContext';
import { useImageRef } from './ImageRefContext';
import { useThread } from './ThreadContext';
import { useCrop } from './CropContext';
import { useRegionEdit } from './RegionEditContext';
import { useMedia } from './MediaContext';
import { useFeedback } from './FeedbackContext';
import { useVideo } from './VideoContext';

export interface PrefillData {
  aspectRatio?: string;
  directions?: { top: boolean; bottom: boolean; left: boolean; right: boolean };
  prompt?: string;
  model?: EditModel;
  enableRegionEdit?: boolean;
}

interface EditorContextValue {
  // State
  historyIndex: number;
  selectedImageInGroup: number;
  setSelectedImageInGroup: (index: number) => void;
  prompts: string[];
  setPrompts: React.Dispatch<React.SetStateAction<string[]>>;
  editModel: EditModel;
  setEditModel: (model: EditModel) => void;
  isLoading: boolean;
  isSubmitting: boolean;
  loadingMessage: string;
  error: string | null;
  setError: (error: string | null) => void;
  editHotspot: { x: number; y: number } | null;
  activeTab: Tab;
  setActiveTab: (tab: Tab) => void;
  isComparing: boolean;
  setIsComparing: (comparing: boolean) => void;
  isImageGalleryOpen: boolean;
  hoveredCombo: PlatformFormatCombo | null;
  onHoverCombo: (combo: PlatformFormatCombo | null) => void;

  // Derived data
  imageGroups: IImageGroup[];
  imagesLoading: boolean;
  currentImage: IImage | null;
  currentImageGroup: IImageGroup | null;
  originalImage: IImage | null;
  canUndo: boolean;
  canRedo: boolean;
  totalImageCount: number;
  isVideoRoute: boolean;
  selectedProjectName: string | undefined;
  userProjects: Project[];
  projectsLoading: boolean;

  // Actions
  handleGenerate: () => void;
  handleExtend: (configs: ExtendingConfig[]) => Promise<void>;
  handleAutoTranslate: (inputLanguage: string, outputLanguages: string[]) => Promise<void>;
  handleManualTranslate: (translations: TranslationRow[]) => Promise<void>;
  handleReformat: (config: { combos: PlatformFormatCombo[]; productRefs?: File[] }) => Promise<void>;
  handleApplyCrop: () => Promise<void>;
  handleApplyAICrop: (aspectsToUse: string[]) => Promise<void>;
  handleUndo: () => void;
  handleRedo: () => void;
  handleReset: () => void;
  handleUploadNew: () => void;
  handleDownload: () => void;
  handleImageClick: (e: React.MouseEvent<HTMLImageElement>) => void;
  handleFileSelect: (files: FileList | null) => void;
  handleImageUpload: (file: File) => Promise<void>;
  handlePromptSubmit: (prompt: string, model: 'fast' | 'detailed') => Promise<void>;
  handleOpenGallery: () => void;
  handleCloseGallery: () => void;
  handleNavigateToImage: (groupIndex: number, imageIndexInGroup?: number) => void;
  handleGalleryDownload: (imageUrl: string, groupIndex: number, imageIndexInGroup?: number) => Promise<void>;
  handleToggleStar: (image: IImage) => void;
  handleDeletePicture: (image: IImage) => Promise<void>;
  handleUpscalePicture: () => Promise<void>;
  handleDownscalePicture: () => Promise<void>;
  handleToggleRegionMode: () => void;
  handleClearHotspot: () => void;
  onThreadDelete: (projectId: string, threadId: string) => Promise<void>;
  onThreadPatch: (projectId: string, threadId: string, newTitle: string) => Promise<void>;
  onThreadCreate: (projectId: string) => void;
  prefill: PrefillData | null;
  setPrefill: (p: PrefillData | null) => void;
  handleCancelGeneration: () => void;
  activeJobId: string | null;
  activeJobs: ActiveJob[];
}

const EditorContext = createContext<EditorContextValue | null>(null);

export const useEditor = (): EditorContextValue => {
  const ctx = useContext(EditorContext);
  if (!ctx) throw new Error('useEditor must be used within an EditorProvider');
  return ctx;
};

interface EditorProviderProps {
  children: React.ReactNode;
  db: any;
  uid: string;
  hasAccessToBulkTreatments: boolean;
}

export const EditorProvider: React.FC<EditorProviderProps> = ({ children, db, uid, hasAccessToBulkTreatments: _hasAccessToBulkTreatments }) => {
  // Context hooks
  const { useBrandGuidelines, excludedAssetIds, setIsExpanded } = useSettings();
  const { imgRef, setImgSize } = useImageRef();
  const { currentProjectId, currentThreadId, selectProjectIdAndThreadId } = useThread();
  const { setCrop, completedCrop, setCompletedCrop, setAspect } = useCrop();
  const { isRegionSelectMode, setIsRegionSelectMode, editRegion, handleClearRegion } = useRegionEdit();
  const { secondaryImages, resetSecondaryImages } = useMedia();
  const { isAnnotationMode, setPendingAnnotation } = useFeedback();
  const {
    setEditorMode, setSelectedVideo, setActiveVideoTab,
    setVideoEditPrompt, setVideoPrompt, setIsVideoLoading,
    setEndFrame, getFinalPrompt,
    _currentImageRef, _setErrorRef, _setLoadingMessageRef,
  } = useVideo();

  // State
  const [historyIndex, setHistoryIndex] = useState<number>(-1);
  const [selectedImageInGroup, setSelectedImageInGroup] = useState<number>(-1);
  const [prompts, setPrompts] = useState<string[]>(['']);
  const [editModel, setEditModel] = useState<EditModel>('google');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [editHotspot, setEditHotspot] = useState<{ x: number; y: number } | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('Editing');
  const [isComparing, setIsComparing] = useState<boolean>(false);
  const [isImageGalleryOpen, setIsImageGalleryOpen] = useState(false);
  const [hoveredCombo, setHoveredCombo] = useState<PlatformFormatCombo | null>(null);
  const [prefill, setPrefill] = useState<PrefillData | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const { status: jobStatus, errorMessage: jobError, clearJob } = useJobStatus(db, currentProjectId, activeJobId);
  const activeJobs = useActiveJobs(db, currentProjectId, uid);

  // Keep VideoContext's cross-context refs in sync
  _setErrorRef.current = setError;
  _setLoadingMessageRef.current = setLoadingMessage;

  // Hooks
  const { projects: userProjects, loading: projectsLoading } = useProjectsWithThreadsRealtime(db, uid, 50);

  const { imageGroups: firestoreImageGroups, loading: imagesLoading, error: imagesError } = useImagesFromFirestore(
    db,
    currentProjectId,
    currentThreadId,
    false
  );

  const imageGroups = firestoreImageGroups;

  // Reset selected image when changing history index or thread,
  // unless handleNavigateToImage explicitly set an image index.
  const skipImageResetRef = useRef(false);
  useEffect(() => {
    if (skipImageResetRef.current) {
      skipImageResetRef.current = false;
    } else {
      setSelectedImageInGroup(-1);
    }
  }, [historyIndex, currentThreadId]);

  // Derived values
  const currentImageGroup = imageGroups[historyIndex] ?? null;
  const currentImage = currentImageGroup && selectedImageInGroup >= 0
    ? currentImageGroup.images[selectedImageInGroup]
    : currentImageGroup?.images.length === 1
      ? currentImageGroup.images[0]
      : null;
  const originalImageGroup = imageGroups[0] ?? null;
  const originalImage = originalImageGroup?.images[0] ?? null;
  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < imageGroups.length - 1;

  // Sync currentImage ref for VideoContext
  _currentImageRef.current = currentImage;

  const location = useLocation();
  const isVideoRoute = location.pathname.includes('/videos/');

  const selectedProjectName = userProjects.find(p => p.project_id === currentProjectId)?.project_name;

  const lastUid = imageGroups[imageGroups.length - 1]?.images[0]?.created_by;
  const isInitialLoad = useRef(true);

  // Reset to last image whenever thread changes
  useEffect(() => {
    if (currentThreadId && imageGroups.length > 0) {
      if (isInitialLoad.current) {
        setHistoryIndex(imageGroups.length - 1);
        isInitialLoad.current = false;
      } else {
        if (lastUid === uid) {
          setHistoryIndex(imageGroups.length - 1);
        }
      }
    }
  }, [currentThreadId, imageGroups.length, lastUid, uid]);

  // Reset the initial load flag when navigating to a different thread
  useEffect(() => {
    isInitialLoad.current = true;
  }, [currentThreadId]);

  // Handle Firestore errors
  useEffect(() => {
    if (imagesError) {
      setError(`Failed to load images: ${imagesError}`);
    }
  }, [imagesError]);

  // React to async job status changes
  useEffect(() => {
    if (!jobStatus || !activeJobId) return;

    if (jobStatus === "completed" || jobStatus === "cancelled") {
      setActiveJobId(null);
      clearJob();
    } else if (jobStatus === "failed") {
      setError(jobError || "Generation failed");
      setActiveJobId(null);
      clearJob();
    }
  }, [jobStatus, jobError, activeJobId, clearJob]);

  // Effect to load image dimensions from currentImage
  useEffect(() => {
    if (currentImage) {
      const img = new Image();
      img.onload = () => {
        setImgSize({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.src = currentImage.output_url;
      return () => URL.revokeObjectURL(currentImage.output_url);
    } else {
      setImgSize(null);
    }
  }, [currentImage, setImgSize]);

  // Reset state when navigating away from a thread
  useEffect(() => {
    if (!currentProjectId || !currentThreadId) {
      setHistoryIndex(-1);
      setError(null);
      setPrompts(['']);
      setVideoPrompt('');
      resetSecondaryImages();
      setEditHotspot(null);
      setActiveTab('Editing');
      setCrop(undefined);
      setCompletedCrop(undefined);
      setIsLoading(false);
      setIsVideoLoading(false);
      setEditModel('google');
      setAspect(undefined);
      setEndFrame(null);
    }
  }, [currentProjectId, currentThreadId, setCrop, setCompletedCrop, setAspect, resetSecondaryImages, setVideoPrompt, setIsVideoLoading, setEndFrame]);

  // Auto-select project when user has only one
  useEffect(() => {
    if (!currentImage && !currentProjectId && !projectsLoading && userProjects.length === 1) {
      selectProjectIdAndThreadId(userProjects[0].project_id, null);
    }
  }, [currentImage, currentProjectId, userProjects, selectProjectIdAndThreadId, projectsLoading]);

  const totalImageCount = useMemo(() => {
    return imageGroups.reduce((sum, group) => sum + group.images.length, 0);
  }, [imageGroups]);

  // --- Callbacks ---

  const handleCancelGeneration = useCallback(async () => {
    if (!activeJobId || !currentProjectId) return;
    try {
      await apiPostCancelGeneration(activeJobId, currentProjectId);
    } catch (err) {
      console.error("Failed to cancel generation:", err);
    }
    setActiveJobId(null);
    clearJob();
  }, [activeJobId, currentProjectId, clearJob]);

  const handleToggleRegionMode = useCallback(() => {
    const newMode = !isRegionSelectMode;
    setIsRegionSelectMode(newMode);
    if (newMode) {
      setEditHotspot(null);
    } else {
      handleClearRegion();
    }
  }, [isRegionSelectMode, setIsRegionSelectMode, handleClearRegion]);

  const onThreadDelete = useCallback(async (projectId: string, threadId: string) => {
    setError(null);
    try {
      await apiDeleteThread(projectId, threadId);
      if (currentProjectId === projectId && currentThreadId === threadId) {
        selectProjectIdAndThreadId(projectId, null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(`Failed to delete the thread. ${errorMessage}`);
      console.error(err);
    }
  }, [currentProjectId, currentThreadId, selectProjectIdAndThreadId]);

  const onThreadPatch = useCallback(async (projectId: string, threadId: string, newTitle: string) => {
    setError(null);
    try {
      await apiPatchThread(projectId, threadId, newTitle);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(`Failed to rename the thread. ${errorMessage}`);
      console.error(err);
    }
  }, []);

  const onThreadCreate = useCallback(async (projectId: string) => {
    selectProjectIdAndThreadId(projectId, null);
    setIsExpanded(false);
    setError(null);
    setPrompts(['']);
    setVideoPrompt('');
    resetSecondaryImages();
    setEditHotspot(null);
    setActiveTab('Editing');
    setCrop(undefined);
    setEndFrame(null);
    setCompletedCrop(undefined);
  }, [selectProjectIdAndThreadId, setIsExpanded, resetSecondaryImages, setCrop, setCompletedCrop, setVideoPrompt, setEndFrame]);

  const handleImageUpload = useCallback(async (file: File) => {
    if (!currentProjectId) {
      setError('Please select or create a project before uploading an image.');
      return;
    }

    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      const sizeMb = (file.size / (1024 * 1024)).toFixed(2);
      setError(`This file is ${sizeMb}MB and exceeds the 10MB limit. Please choose a smaller image.`);
      return;
    }

    setError(null);
    setIsLoading(true);
    setLoadingMessage('Uploading image...');
    try {
      const newImageJson = await apiPostStartThread(file, currentProjectId);
      selectProjectIdAndThreadId(newImageJson.project_id, newImageJson.thread_id);
      setHistoryIndex(0);
      setEditHotspot(null);
      setActiveTab('Editing');
      setCrop(undefined);
      setCompletedCrop(undefined);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(`Failed to upload the image. ${errorMessage}`);
      console.error(err);
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  }, [currentProjectId, selectProjectIdAndThreadId, setCrop, setCompletedCrop]);

  const handlePromptSubmit = useCallback(async (prompt: string, model: 'fast' | 'detailed') => {
    if (!currentProjectId) {
      setError('Please select or create a project before generating an image.');
      return;
    }

    if (!prompt.trim()) {
      setError('Please enter a description for your image.');
      return;
    }

    setError(null);
    setIsLoading(true);
    setLoadingMessage('AI is generating your image...');
    try {
      const newImageJson = await apiPostStartThreadWithPrompt(prompt, currentProjectId, model);
      selectProjectIdAndThreadId(newImageJson.project_id, newImageJson.thread_id);
      setHistoryIndex(0);
      setEditHotspot(null);
      setActiveTab('Editing');
      setCrop(undefined);
      setCompletedCrop(undefined);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(`Failed to generate the image. ${errorMessage}`);
      console.error(err);
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  }, [currentProjectId, selectProjectIdAndThreadId, setCrop, setCompletedCrop]);

  const handleGenerate = useCallback(async () => {
    if (!currentImage) {
      setError('No image loaded to edit.');
      return;
    }

    const nonEmptyPrompts = prompts.filter(p => p.trim());
    if (nonEmptyPrompts.length === 0) {
      setError('Please enter a description for your edit.');
      return;
    }

    if (!currentProjectId || !currentThreadId) {
      setError('Project or Thread ID is missing.');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const sharedParams = {
        project_id: currentProjectId,
        thread_id: currentThreadId,
        picture_id: currentImage.picture_id,
        prompts: nonEmptyPrompts.map((p) => getFinalPrompt(p)),
        edit_hotspot_x: editHotspot?.x,
        edit_hotspot_y: editHotspot?.y,
        additionnal_images: secondaryImages.map((x) => x.file),
        additionnal_images_categories: secondaryImages.map((x) => x.category || "reference"),
        use_brand_guidelines: useBrandGuidelines,
        excluded_asset_ids: excludedAssetIds,
      };
      const { job_id } = editRegion
        ? await apiPostRegionalEdit({
            ...sharedParams,
            edit_region_json: JSON.stringify(editRegion),
          })
        : await apiPostGenerate({
            ...sharedParams,
            operation: "inpaint",
            model: editModel,
          });
      setActiveJobId(job_id);
      setEditHotspot(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(`Failed to start generation. ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [currentImage, editModel, prompts, editHotspot, secondaryImages, currentProjectId, currentThreadId, getFinalPrompt, useBrandGuidelines, excludedAssetIds, editRegion, setIsRegionSelectMode, handleClearRegion]);

  const handleExtend = useCallback(async (configs: ExtendingConfig[]) => {
    if (!currentImage) {
      setError('No image loaded to edit.');
      return;
    }

    if (!currentProjectId || !currentThreadId) {
      setError('Project or Thread ID is missing.');
      return;
    }

    const formattedConfigs = configs.map(config => ({
      directions: Object.keys(config.directions).filter(dir => config.directions[dir as keyof ExtendingConfig['directions']]),
      outputSize: {
        width: config.outputSize.width,
        height: config.outputSize.height,
      },
    }));

    setError(null);
    setIsSubmitting(true);

    try {
      const { job_id } = await apiPostGenerate({
        operation: "outpaint",
        project_id: currentProjectId,
        thread_id: currentThreadId,
        picture_id: currentImage.picture_id,
        extending_configs_json: JSON.stringify(formattedConfigs),
        model: editModel,
        use_brand_guidelines: useBrandGuidelines,
        excluded_asset_ids: excludedAssetIds,
      });
      setActiveJobId(job_id);
      setEditHotspot(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(`Failed to start generation. ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [currentImage, editModel, currentProjectId, currentThreadId, useBrandGuidelines, excludedAssetIds]);

  const handleManualTranslate = useCallback(async (translationRows: TranslationRow[]) => {
    if (!currentImage) {
      setError('No image loaded to edit.');
      return;
    }

    if (!currentProjectId || !currentThreadId) {
      setError('Project or Thread ID is missing.');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const { job_id } = await apiPostGenerate({
        operation: "manual_translate",
        project_id: currentProjectId,
        thread_id: currentThreadId,
        picture_id: currentImage.picture_id,
        translation_rows_json: JSON.stringify(translationRows.map((r) => ({
          original: r.original,
          target: r.target,
          group_id: r.group_id,
        }))),
        model: editModel,
        edit_region_json: editRegion ? JSON.stringify(editRegion) : undefined,
      });
      setActiveJobId(job_id);
      setEditHotspot(null);
      setIsRegionSelectMode(false);
      handleClearRegion();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(`Failed to start generation. ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [currentImage, editModel, currentProjectId, currentThreadId, editRegion, setIsRegionSelectMode, handleClearRegion]);

  const handleAutoTranslate = useCallback(async (inputLanguage: string, outputLanguages: string[]) => {
    if (!currentImage) {
      setError('No image loaded to edit.');
      return;
    }

    if (outputLanguages.length === 0) {
      setError('Please select at least one output language.');
      return;
    }

    if (!currentProjectId || !currentThreadId) {
      setError('Project or Thread ID is missing.');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const { job_id } = await apiPostGenerate({
        operation: "translate",
        project_id: currentProjectId,
        thread_id: currentThreadId,
        picture_id: currentImage.picture_id,
        input_language: inputLanguage,
        output_languages: outputLanguages,
        model: editModel,
        edit_region_json: editRegion ? JSON.stringify(editRegion) : undefined,
      });
      setActiveJobId(job_id);
      setEditHotspot(null);
      setIsRegionSelectMode(false);
      handleClearRegion();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(`Failed to start generation. ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [currentImage, editModel, currentProjectId, currentThreadId, editRegion, setIsRegionSelectMode, handleClearRegion]);

  const handleReformat = useCallback(async (config: { combos: PlatformFormatCombo[]; productRefs?: File[] }) => {
    if (!currentImage) { setError('No image loaded to edit.'); return; }
    if (!currentProjectId || !currentThreadId) { setError('Project or Thread ID is missing.'); return; }

    setError(null);
    setIsSubmitting(true);

    try {
      const { job_id } = await apiPostReformat({
        project_id: currentProjectId,
        thread_id: currentThreadId,
        picture_id: currentImage.picture_id,
        combos: config.combos.map((c) =>
          JSON.stringify({
            label: c.label,
            platform_label: c.platformLabel,
            aspect_ratio: c.aspectRatio,
            top_margin_pct: c.margins.top,
            left_margin_pct: c.margins.left,
            right_margin_pct: c.margins.right,
            bottom_margin_pct: c.margins.bottom,
            ...(c.targetWidth && c.targetHeight ? { target_width: c.targetWidth, target_height: c.targetHeight } : {}),
          })
        ),
        use_brand_guidelines: useBrandGuidelines,
        excluded_asset_ids: excludedAssetIds,
        additionnal_images: config.productRefs,
        additionnal_images_categories: config.productRefs?.map(() => "product"),
      });
      setActiveJobId(job_id);
      setEditHotspot(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(`Failed to start generation. ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [currentImage, currentProjectId, currentThreadId, useBrandGuidelines, excludedAssetIds]);

  const handleApplyCrop = useCallback(async () => {
    if (!currentImage) {
      setError('No image loaded to edit.');
      return;
    }

    if (!completedCrop || !imgRef.current) {
      setError('Please select a zone to crop.');
      return;
    }

    if (!currentProjectId || !currentThreadId) {
      setError('Project or Thread ID is missing.');
      return;
    }

    const image = imgRef.current;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    setIsLoading(true);
    setLoadingMessage('Cropping image...');
    setError(null);

    try {
      await apiPostCropImage(
        currentImage.picture_id,
        Math.round(completedCrop.x * scaleX),
        Math.round(completedCrop.y * scaleY),
        Math.round(completedCrop.width * scaleX),
        Math.round(completedCrop.height * scaleY),
        window.devicePixelRatio || 1,
        currentProjectId,
        currentThreadId
      );
      setCompletedCrop(undefined);
      setCrop(undefined);
      setEditHotspot(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(`Failed to generate the image. ${errorMessage}`);
      console.error(err);
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  }, [currentImage, completedCrop, currentProjectId, currentThreadId, imgRef, setCompletedCrop, setCrop]);

  const handleApplyAICrop = useCallback(async (aspectsToUse: string[]) => {
    if (!currentImage) {
      setError('No image loaded to edit.');
      return;
    }

    if (aspectsToUse.length === 0) {
      setError('Please select an aspect ratio for AI Crop.');
      return;
    }

    if (!currentProjectId || !currentThreadId) {
      setError('Project or Thread ID is missing.');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const { job_id } = await apiPostGenerate({
        operation: "ai_crop",
        project_id: currentProjectId,
        thread_id: currentThreadId,
        picture_id: currentImage.picture_id,
        formats: aspectsToUse.join(","),
      });
      setActiveJobId(job_id);
      setCompletedCrop(undefined);
      setCrop(undefined);
      setEditHotspot(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(`Failed to start generation. ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [currentImage, currentProjectId, currentThreadId, setCompletedCrop, setCrop]);

  const handleUndo = useCallback(() => {
    if (canUndo) {
      setHistoryIndex(historyIndex - 1);
      setEditHotspot(null);
    }
  }, [canUndo, historyIndex]);

  const handleRedo = useCallback(() => {
    if (canRedo) {
      setHistoryIndex(historyIndex + 1);
      setEditHotspot(null);
    }
  }, [canRedo, historyIndex]);

  const handleReset = useCallback(() => {
    if (imageGroups.length > 0) {
      setHistoryIndex(0);
      setError(null);
      setEditHotspot(null);
    }
  }, [imageGroups.length]);

  const handleUploadNew = useCallback(() => {
    selectProjectIdAndThreadId(null, null);
    setHistoryIndex(-1);
    setError(null);
    setPrompts(['']);
    setVideoPrompt('');
    setEditHotspot(null);
    // Reset video editor state
    setEditorMode('picture');
    setSelectedVideo(null);
    setVideoEditPrompt('');
    setActiveVideoTab('Editing');
  }, [selectProjectIdAndThreadId, setVideoPrompt, setEditorMode, setSelectedVideo, setVideoEditPrompt, setActiveVideoTab]);

  const handleDownload = useCallback(async () => {
    if (currentImageGroup && currentImageGroup.images.length > 1 && selectedImageInGroup === -1) {
      try {
        setIsLoading(true);
        setLoadingMessage('Creating ZIP archive...');

        const zip = new JSZip();
        const timestamp = Date.now();

        for (let i = 0; i < currentImageGroup.images.length; i++) {
          const img = currentImageGroup.images[i];
          const response = await fetch(img.output_url);
          const blob = await response.blob();

          const ext = blob.type.includes('png') ? 'png' :
                     blob.type.includes('jpeg') || blob.type.includes('jpg') ? 'jpg' :
                     'png';

          zip.file(`image_${i + 1}.${ext}`, blob);
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `images_group_${currentImageGroup.seq}_${timestamp}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

      } catch (err) {
        console.error('Failed to create ZIP archive:', err);
        setError('Failed to download images as ZIP');
      } finally {
        setIsLoading(false);
        setLoadingMessage('');
      }
    } else if (currentImage) {
      await downloadFromSignedUrl(currentImage.output_url, `edited-${Date.now()}.png`);
    }
  }, [currentImage, currentImageGroup, selectedImageInGroup]);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (files && files[0]) {
      handleImageUpload(files[0]);
    }
  }, [handleImageUpload]);

  // Handle image click with regional editing support
  const handleImageClick = useCallback((e: React.MouseEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const rect = img.getBoundingClientRect();
    const { naturalWidth, naturalHeight, clientWidth, clientHeight } = img;

    const imgRatio = naturalWidth / naturalHeight;
    const elementRatio = clientWidth / clientHeight;

    let paintedWidth, paintedHeight;
    if (elementRatio > imgRatio) {
      paintedHeight = clientHeight;
      paintedWidth = paintedHeight * imgRatio;
    } else {
      paintedWidth = clientWidth;
      paintedHeight = paintedWidth / imgRatio;
    }

    const paintedOffsetX = (clientWidth - paintedWidth) / 2;
    const paintedOffsetY = (clientHeight - paintedHeight) / 2;

    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    if (clickX < paintedOffsetX || clickX > paintedOffsetX + paintedWidth ||
        clickY < paintedOffsetY || clickY > paintedOffsetY + paintedHeight) {
      return;
    }

    const relativeToImageX = clickX - paintedOffsetX;
    const relativeToImageY = clickY - paintedOffsetY;

    if (isAnnotationMode) {
      const relativeX = relativeToImageX / paintedWidth;
      const relativeY = relativeToImageY / paintedHeight;
      setPendingAnnotation({ x: relativeX, y: relativeY });
      return;
    }

    if (activeTab !== 'Editing' && activeTab !== 'Translating') return;

    const scaleX = naturalWidth / paintedWidth;
    const scaleY = naturalHeight / paintedHeight;

    const originalX = Math.round(relativeToImageX * scaleX);
    const originalY = Math.round(relativeToImageY * scaleY);

    if (isRegionSelectMode) {
      return;
    }

    setEditHotspot({ x: originalX, y: originalY });
  }, [isAnnotationMode, activeTab, isRegionSelectMode, setPendingAnnotation]);

  const handleClearHotspot = useCallback(() => {
    setEditHotspot(null);
  }, []);

  const handleOpenGallery = useCallback(() => {
    setIsImageGalleryOpen(true);
  }, []);

  const handleCloseGallery = useCallback(() => {
    setIsImageGalleryOpen(false);
  }, []);

  const handleNavigateToImage = useCallback((groupIndex: number, imageIndexInGroup?: number) => {
    if (imageIndexInGroup !== undefined) {
      skipImageResetRef.current = true;
    }
    setHistoryIndex(groupIndex);
    if (imageIndexInGroup !== undefined) {
      setSelectedImageInGroup(imageIndexInGroup);
    } else {
      setSelectedImageInGroup(-1);
    }
  }, []);

  const handleGalleryDownload = useCallback(async (imageUrl: string, groupIndex: number, imageIndexInGroup?: number) => {
    await downloadFromSignedUrl(imageUrl, `image-${groupIndex}-${imageIndexInGroup ?? 0}-${Date.now()}.png`);
  }, []);

  const handleToggleStar = useCallback((image: IImage) => {
    togglePictureStarred(db, image.project_id, image.thread_id, image.picture_id, image.starred ?? false)
      .catch((err) => console.error('Failed to toggle star:', err));
  }, [db]);

  const handleDeletePicture = useCallback(async (image: IImage) => {
    const result = await apiDeletePicture(image.project_id, image.thread_id, image.picture_id);
    if (result?.thread_deleted) {
      selectProjectIdAndThreadId(image.project_id, null);
    }
  }, [selectProjectIdAndThreadId]);

  const handleUpscalePicture = useCallback(async () => {
    if (!currentImage) {
      setError('No image loaded to edit.');
      return;
    }

    if (!currentProjectId || !currentThreadId) {
      setError('Project or Thread ID is missing.');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const { job_id } = await apiPostGenerate({
        operation: "upscale",
        project_id: currentProjectId,
        thread_id: currentThreadId,
        picture_id: currentImage.picture_id,
        factor: "x2",
      });
      setActiveJobId(job_id);
      setEditHotspot(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(`Failed to upscale the image. ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [currentImage, currentProjectId, currentThreadId]);

  const handleDownscalePicture = useCallback(async () => {
    if (!currentImage) {
      setError('No image loaded to edit.');
      return;
    }

    if (!currentProjectId || !currentThreadId) {
      setError('Project or Thread ID is missing.');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const { job_id } = await apiPostGenerate({
        operation: "downscale",
        project_id: currentProjectId,
        thread_id: currentThreadId,
        picture_id: currentImage.picture_id,
        factor: "0.5",
      });
      setActiveJobId(job_id);
      setEditHotspot(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(`Failed to downscale the image. ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [currentImage, currentProjectId, currentThreadId]);

  return (
    <EditorContext.Provider value={{
      historyIndex,
      selectedImageInGroup,
      setSelectedImageInGroup,
      prompts,
      setPrompts,
      editModel,
      setEditModel,
      isLoading,
      isSubmitting,
      loadingMessage,
      error,
      setError,
      editHotspot,
      activeTab,
      setActiveTab,
      isComparing,
      setIsComparing,
      isImageGalleryOpen,
      hoveredCombo,
      onHoverCombo: setHoveredCombo,
      prefill,
      setPrefill,
      imageGroups,
      imagesLoading,
      currentImage,
      currentImageGroup,
      originalImage,
      canUndo,
      canRedo,
      totalImageCount,
      isVideoRoute,
      selectedProjectName,
      userProjects,
      projectsLoading,
      handleGenerate,
      handleExtend,
      handleAutoTranslate,
      handleManualTranslate,
      handleReformat,
      handleApplyCrop,
      handleApplyAICrop,
      handleUndo,
      handleRedo,
      handleReset,
      handleUploadNew,
      handleDownload,
      handleImageClick,
      handleFileSelect,
      handleImageUpload,
      handlePromptSubmit,
      handleOpenGallery,
      handleCloseGallery,
      handleNavigateToImage,
      handleGalleryDownload,
      handleToggleStar,
      handleDeletePicture,
      handleUpscalePicture,
      handleDownscalePicture,
      handleToggleRegionMode,
      handleClearHotspot,
      onThreadDelete,
      onThreadPatch,
      onThreadCreate,
      handleCancelGeneration,
      activeJobId,
      activeJobs,
    }}>
      {children}
    </EditorContext.Provider>
  );
};
